import { useEffect, useRef, useState, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { LandingPage } from "@/pages/LandingPage";
import { Dashboard } from "@/pages/Dashboard";
import { Documents } from "@/pages/Documents";
import { Feasibility } from "@/pages/Feasibility";
import { FeasibilityStudio } from "@/pages/FeasibilityStudio";
import { Prefeasibility } from "@/pages/Prefeasibility";
import { Members } from "@/pages/Members";
import { Voting } from "@/pages/Voting";
import { Process79A } from "@/pages/Process79A";
import { SocietySetup } from "@/pages/SocietySetup";
import { Builders } from "@/pages/Builders";
import { PMCSelection } from "@/pages/PMCSelection";
import { ClaimSociety } from "@/pages/ClaimSociety";
import { AdminDashboard } from "@/pages/AdminDashboard";
import { MemberRegister } from "@/pages/MemberRegister";
import { MemberRegistrations } from "@/pages/MemberRegistrations";
import Services from "@/pages/Services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const response = await fetch(`${API}/auth/me`, { credentials: "include" });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        return userData;
      } else {
        setUser(null);
        return null;
      }
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
      setAuthChecked(true);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Store where user came from
    sessionStorage.setItem('auth_redirect', window.location.pathname);
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try {
      await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    } catch (e) {
      console.error("Logout error:", e);
    }
    setUser(null);
    setAuthChecked(false);
    window.location.href = "/";
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, logout, checkAuth, authChecked }}>
      {children}
    </AuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await fetch(`${API}/auth/session`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ session_id: sessionId }),
            credentials: "include"
          });
          
          if (response.ok) {
            const userData = await response.json();
            setUser(userData);
            // Clear the hash and navigate
            window.history.replaceState(null, '', '/dashboard');
            navigate("/dashboard", { replace: true });
          } else {
            console.error("Auth session failed");
            navigate("/", { replace: true });
          }
        } catch (error) {
          console.error("Auth callback error:", error);
          navigate("/", { replace: true });
        }
      } else {
        navigate("/", { replace: true });
      }
    };

    processAuth();
  }, [location, navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="spinner mx-auto mb-4"></div>
        <p className="text-slate-600">Signing you in...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading, authChecked, checkAuth } = useAuth();
  const location = useLocation();
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    // If auth hasn't been checked yet and we're not loading, check it
    if (!authChecked && !loading && !checking) {
      setChecking(true);
      checkAuth().finally(() => setChecking(false));
    }
  }, [authChecked, loading, checking, checkAuth]);

  if (loading || checking || !authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/" state={{ from: location }} replace />;
  }

  return children;
};

// App Router
const AppRouter = () => {
  const location = useLocation();

  // Check for session_id during render (not in useEffect)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/setup" element={<ProtectedRoute><SocietySetup /></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
      <Route path="/feasibility" element={<ProtectedRoute><Feasibility /></ProtectedRoute>} />
      <Route path="/feasibility-studio" element={<ProtectedRoute><FeasibilityStudio /></ProtectedRoute>} />
      <Route path="/prefeasibility" element={<ProtectedRoute><Prefeasibility /></ProtectedRoute>} />
      <Route path="/members" element={<ProtectedRoute><Members /></ProtectedRoute>} />
      <Route path="/voting" element={<ProtectedRoute><Voting /></ProtectedRoute>} />
      <Route path="/builders" element={<ProtectedRoute><Builders /></ProtectedRoute>} />
      <Route path="/pmc" element={<ProtectedRoute><PMCSelection /></ProtectedRoute>} />
      <Route path="/process79a" element={<ProtectedRoute><Process79A /></ProtectedRoute>} />
      <Route path="/claim" element={<ProtectedRoute><ClaimSociety /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
      <Route path="/services" element={<ProtectedRoute><Services /></ProtectedRoute>} />
      <Route path="/registrations/:societyId" element={<ProtectedRoute><MemberRegistrations /></ProtectedRoute>} />
      <Route path="/register/:societyId" element={<MemberRegister />} />
    </Routes>
  );
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
